import { EnglishLevelsType } from '@/features/Result/components/EnglishLevelBar.tsx'
import api from '@/services/api'

export enum CefrLevel {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2'
}

export type VocabularyDataOverview = {
  sentenceCount: number
  wordCount: number
  uniqueWordCount: number
  averageWordCountBySentence: number
  averageUniqueWordCountBySentence: number
  syllableCount: number
  wordCountWithTwoOrMoreSyllables: number
  wordsByCefrLevel: {
    [x in CefrLevel]: {
      word: string
      cefrLevel: string
      partOfSpeech: string
    }[]
  }
}

export type ChatItemGrammarErrorItem = {
  correctionType: string
  endIndex: number
  errorDescription: string
  errorType: string
  originalText: string
  startIndex: number
  suggestion: string
}

export type ChatItem = {
  _id: string
  content: string
  timeline: string
  role: 'user' | 'assistant' | 'hidden-chat'
  audioUrl: string
  recordUrl: string
  duration: number
  grammarErrors: ChatItemGrammarErrorItem[]
  fluencyData: {
    word_score_list: { word: string; ending_punctuation: string }[]
    fluency: {
      overall_metrics: {
        all_pause_list: number[][]
      }
    }
  }

  //UI only
  amount: number
}

export type FluencyTestResult = {
  fluencyDataOverview: {
    cefrScore: {
      overall: EnglishLevelsType
      grammar: EnglishLevelsType
      coherence: EnglishLevelsType
      pronunciation: EnglishLevelsType
      vocabulary: EnglishLevelsType
      fluency: EnglishLevelsType
    }
    grammarMetrics: {
      grammaticalAccuracy: number
      grammaticalRange: number
      length: number
      lexicalDiversity: number
    }
    vocabularyMetrics: {
      academicLanguageUse: number
      collocationCommonality: number
      idiomaticity: number
      lexicalDiversity: number
      wordSophistication: number
      wordSpecificity: number
    }
    coherenceMetrics: {
      adverbDiversity: number
      basicConnectives: number
      causalConnectives: number
      lexicalDensity: number
      negativeConnectives: number
      pronounDensity: number
      verbDiversity: number
    }
    fluencyMetrics: {
      allPauseCount: number
      allPauseDuration: number
      articulationRate: number
      duration: number
      maxLengthRun: number
      speechRate: number
      syllableCorrectPerMinute: number
      wordCorrectPerMinute: number
    }
    score: {
      pronunciation: number
      fluency: number
      grammar: number
      coherence: number
      vocabulary: number
      overall: number
    }
    speechaceScore: {
      pronunciation: number
      fluency: number
      grammar: number
      coherence: number
      vocabulary: number
      overall: number
    }
  }
  vocabularyDataOverview: VocabularyDataOverview
  chatGptOverview: {
    reasons: string[]
  }
  textInspectorData: {
    lexicalResult: unknown[]
    wordsLists: unknown[]
    statisticsResult: unknown
  }
  chat: ChatItem[]
  finishedAt: string
  locale?: 'pt' | 'en' | 'es'
  status: 'PENDING' | 'PROCESSING' | 'FINISHED'
  videoRecordUrl?: string
  student: { name: string; company: { whiteLogoUrl?: string } }
}

export async function getFluencyTestResult(
  code: string
): Promise<FluencyTestResult> {
  const { data } = await api.get(`/fluency-test/${code}`)
  return data
}
